import { Button } from '@material-ui/core';
import { useTranslation } from 'react-i18next';
import { Navigate, useNavigate, useLocation } from 'react-router-dom';
import { Formik } from 'formik';
import { enqueueSnackbar } from 'notistack';

import { useAuth } from './use-auth';
import Layout from './Layout';
import TextField from './TextField';
import AuthForm from './AuthFormContainer';
import SubmitButtonsContainer from '../common/SubmitButtonsContainer';
import PasswordValidations from './PasswordValidations';
import usePasswordRequirements from './usePasswordRequirements';
import SubmitErrorHelper from '../common/SubmitErrorHelper';

export default function NewPassword() {
    const { t } = useTranslation();

    const navigate = useNavigate();

    const { search } = useLocation();
    const query = new URLSearchParams(search);
    const userName = query.get('userName');
    const token = query.get('token');

    const { confirmPasswordReset } = useAuth();
    const verifyPasswordRequirements = usePasswordRequirements();

    const validate = values => {
        let errors = {};

        if (!values.password) {
            errors.password = t('ChangePassword.PasswordRequired');
        } else {
            errors = { ...errors, ...verifyPasswordRequirements(values.password) };
        }

        if (!values.confirmPassword) {
            errors.confirmPassword = t('ChangePassword.ConfirmPasswordRequired');
        } else if (values.confirmPassword !== values.password) {
            errors.confirmPassword = t('ChangePassword.PasswordsMustMatch');
        }

        return errors;
    };

    const onSubmit = async (values, { setSubmitting, setErrors }) => {
        try {
            let isPasswordChanged = await confirmPasswordReset(userName, values.password, token);
            if (isPasswordChanged) {
                enqueueSnackbar(t('ChangePassword.PasswordChanged'), { variant: 'success' });
                return navigate('/login');
            }
            setErrors({ submitError: t('ChangePassword.LinkExpired') });
        } catch {
            setErrors({ submitError: t('UnknownError') });
        }
        setSubmitting(false);
    };

    const gotoLoginPage = async () => {
        return navigate('/login');
    };

    if (!userName || !token) {
        return <Navigate to='/login' />;
    }

    return (
        <Layout title={t('ChangePassword.ChangePassword')}>
            <Formik
                validate={validate}
                onSubmit={onSubmit}
                validateOnBlur={false}
                initialValues={initialValues}
            >
                {({ values, errors }) => (
                    <AuthForm>
                        <TextField
                            name='password'
                            label={t('ChangePassword.NewPassword')}
                            type='password'
                            autoFocus
                            autoComplete='new-password'
                        />
                        <TextField
                            name='confirmPassword'
                            label={t('ChangePassword.ConfirmPassword')}
                            type='password'
                            autoComplete='new-password'
                        />

                        <PasswordValidations errors={errors} hasValue={!!values.password} />

                        <SubmitErrorHelper>{errors.submitError}</SubmitErrorHelper>
                        <SubmitButtonsContainer>
                            <Button variant='contained' onClick={gotoLoginPage}>
                                {t('Cancel')}
                            </Button>
                            <Button type='submit' variant='contained' color='primary'>
                                {t('Ok')}
                            </Button>
                        </SubmitButtonsContainer>
                    </AuthForm>
                )}
            </Formik>
        </Layout>
    );
}

const initialValues = {
    password: '',
    confirmPassword: ''
};
